<template>
    <Head :title="isSelfMode ? 'Start a self-assessment' : 'Start a 360'" />
    <BaseLayout>
        <a href="/reflections" class="self-start body-small text-valence-grey-750 hover:text-valence-grey-850 flex items-center gap-1">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Reflections
        </a>

        <BasePageHeader :title="isSelfMode ? 'Start a self-assessment' : 'Start a 360'">
            <template #subtitle>
                <template v-if="isSelfMode">Reflect on your own strengths and areas for growth. {{ questionCountLabel }}</template>
                <template v-else-if="hasCategoryThresholds">
                    Anonymous feedback from your peers, your manager, and direct reports. {{ questionCountLabel }}, ~15 minutes for raters.
                </template>
                <template v-else-if="minResponses === null">Anonymous feedback from your peers, your manager, and direct reports. ~15 minutes for raters.</template>
                <template v-else-if="minResponses === 0">
                    Anonymous feedback from your peers, your manager, and direct reports. {{ questionCountLabel }}, ~15 minutes for raters. Your report is ready as soon as you
                    launch.
                </template>
                <template v-else>
                    Anonymous feedback from your peers, your manager, and direct reports. {{ questionCountLabel }}, ~15 minutes for raters. Your report is ready when
                    {{ minResponses }}+ raters respond.
                </template>
            </template>
        </BasePageHeader>

        <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <template v-else>
            <BaseCard v-if="templates.length > 1" padding="md">
                <label for="template-select" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Template</span>
                    <select
                        id="template-select"
                        v-model="selectedTemplateId"
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small">
                        <option v-for="templateOption in templates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                    </select>
                    <span v-if="!isSelfMode && unlockNote" class="block details text-valence-grey-750 mt-1">{{ unlockNote }}</span>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode" padding="md">
                <label for="self-assess" class="flex items-start gap-3 cursor-pointer">
                    <input id="self-assess" v-model="selfAssess" type="checkbox" class="mt-1" />
                    <span>
                        <span class="block body-small-bold text-valence-grey-850">Also complete the assessment yourself</span>
                        <span class="block body-small text-valence-grey-750 mt-0.5"
                            >Lets you see how your view of yourself compares to others'. We'll show the gaps in your report.</span
                        >
                    </span>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode" padding="md">
                <label for="personal-note" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Personal note <span class="text-valence-grey-750 font-normal">(optional)</span></span>
                    <textarea
                        id="personal-note"
                        v-model="personalNote"
                        rows="3"
                        placeholder="Hey — I'd really value your honest take. It's anonymous, and it'll only take ~15 minutes. Thank you."
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 resize-none body-small"></textarea>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode" theme="muted" padding="md" class="flex items-start gap-3">
                <i class="bi bi-shield-check text-green-700 mt-0.5" aria-hidden="true"></i>
                <div class="body-small text-valence-grey-850">
                    <div class="body-small-bold">Always anonymous</div>
                    Results are aggregated. Cuts by relationship show only when 3+ people in that group respond — otherwise we combine everything.
                </div>
            </BaseCard>

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <div class="flex justify-end">
                <BaseButton theme="primary" :disabled="creating" @click="onCreate">{{ creating ? "Creating…" : isSelfMode ? "Start self-assessment" : "Create cycle" }}</BaseButton>
            </div>
        </template>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { StartCycleContextResponse, TemplateSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";

const isSelfMode = new URLSearchParams(globalThis.location.search).get("mode") === "self";

const templates = ref<TemplateSummary[]>([]);
const selectedTemplateId = ref<string | null>(null);
const defaultMinResponses = ref<number | null>(null);
const selectedTemplate = computed(() => templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value) ?? null);
const minResponses = computed<number | null>(() => selectedTemplate.value?.min_responses ?? defaultMinResponses.value);
const questionCount = computed<number | null>(() => {
    const selected = templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value);
    return selected?.question_count ?? null;
});
const questionCountLabel = computed<string>(() => {
    const n = questionCount.value;
    if (n === null) {
        return "";
    }
    return n === 1 ? "1 short question" : `${n} short questions`;
});

const loading = ref(true);
const loadError = ref<string | null>(null);

const selfAssess = ref(true);
// Category thresholds replace the overall minimum, so neither the subtitle nor
// the picker note can promise an unlock here. The nomination step spells the
// thresholds out instead.
const hasCategoryThresholds = computed(() => (selectedTemplate.value?.category_thresholds?.thresholds?.length ?? 0) > 0);
const unlockNote = computed(() => (hasCategoryThresholds.value ? null : `Results unlock after ${minResponses.value}+ responses.`));

const personalNote = ref("");
const creating = ref(false);
const error = ref<string | null>(null);

onMounted(async () => {
    const startContextUrl = isSelfMode ? "/v1/reflections/start-context?mode=self" : "/v1/reflections/start-context";
    const { data, error: fetchError } = await useFetch(startContextUrl).json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || (isSelfMode ? "Could not start a self-assessment." : "Could not start a 360.");
        return;
    }
    const ctx = (data.value as StartCycleContextResponse).context;
    defaultMinResponses.value = ctx.min_responses;
    templates.value = ctx.templates;
    selectedTemplateId.value = ctx.default_template_id ?? null;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_START_VIEWED, {
        template_count: ctx.templates.length,
        has_default_template: ctx.default_template_id !== null,
        mode: isSelfMode ? "self_assessment" : "360",
    });
});

async function onCreate() {
    creating.value = true;
    error.value = null;
    try {
        const { data, error: fetchError } = await useFetch("/v1/reflections/cycles")
            .post(
                JSON.stringify({
                    template_id: selectedTemplateId.value,
                    is_self_assessment_only: isSelfMode,
                    self_assess_enabled: isSelfMode ? true : selfAssess.value,
                    personal_note: isSelfMode ? "" : personalNote.value,
                }),
                "application/json",
            )
            .json();

        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || "Could not create cycle. Please try again.";
            return;
        }

        const cycle = (data.value as { cycle: { id: string; is_self_assessment_only: boolean } }).cycle;
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CYCLE_CREATED, {
            cycle_id: cycle.id,
            template_id: selectedTemplateId.value,
            is_self_assessment_only: isSelfMode,
            self_assess_enabled: isSelfMode ? true : selfAssess.value,
            has_personal_note: personalNote.value.trim().length > 0,
        });

        if (isSelfMode) {
            // Self-assessment cycles are auto-launched by the backend in the
            // same create request, so we go straight into taking the assessment.
            router.visit(`/reflections/360/${cycle.id}/self`);
        } else {
            router.visit(`/reflections/360/${cycle.id}/nominate`);
        }
    } finally {
        creating.value = false;
    }
}
</script>
